import posthog from 'posthog-js';
import type { CaptureResult } from 'posthog-js';

const POSTHOG_KEY = (import.meta.env.VITE_POSTHOG_KEY as string)?.trim();
const POSTHOG_HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined)?.trim() || 'https://eu.i.posthog.com';
// Only set in prod, where nginx proxies /ph-assets/ to PostHog's static asset
// CDN; left unset in dev so posthog-js falls back to fetching assets directly.
const POSTHOG_ASSET_HOST = (import.meta.env.VITE_POSTHOG_ASSET_HOST as string | undefined)?.trim() || undefined;

let initialized = false;

// Instagram/Facebook's Android in-app browser injects its own JS<->Java
// postMessage bridge. When the person closes that in-app browser (or Android
// tears down its WebView) while the bridge is mid-call, it throws this from
// the host app's own native code -- nothing on this page can cause or catch
// it, so it's dropped here instead of showing up as noise in error tracking.
function dropKnownNoise(result: CaptureResult | null): CaptureResult | null {
  if (!result || result.event !== '$exception') return result;
  const message = String(result.properties?.$exception_message ?? '');
  if (message.includes('Java object is gone')) return null;
  return result;
}

export function getPostHogClient() {
  if (!initialized) {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      asset_host: POSTHOG_ASSET_HOST,
      capture_pageview: false,
      capture_exceptions: true,
      before_send: dropKnownNoise,
      logs: {
        captureConsoleLogs: true,
      },
      persistence: 'localStorage+cookie',
      cross_subdomain_cookie: false,
      person_profiles: 'identified_only',
      disable_surveys: true,
    });
    initialized = true;
  }

  return posthog;
}

export function captureSpaPageView(pathname: string, search: string, hash: string): void {
  const client = getPostHogClient();
  if (!client) {
    return;
  }

  client.capture('$pageview', {
    pathname,
    search,
    hash,
    url: `${pathname}${search}${hash}`,
  });
}

export function captureSpaTabView(tab: string, properties?: Record<string, string>): void {
  const client = getPostHogClient();
  if (!client || !tab.trim()) {
    return;
  }

  client.capture('spa_tab_view', {
    tab: tab.trim(),
    ...properties,
  });
}

export function identifyAnalyticsUser(userId: string, properties?: Record<string, string>): void {
  const client = getPostHogClient();
  if (!client || !userId.trim()) {
    return;
  }
  client.identify(userId.trim(), properties);
}

export function resetAnalyticsUser(): void {
  const client = getPostHogClient();
  if (!client) {
    return;
  }
  client.reset();
}

/** One of the steps the site wants its visitors to reach that no page view
 * shows: a transcription opened, a story chosen from a card, a story listened
 * to. Named once here so PostHog's funnels can rely on the names. */
export type GoalEvent = 'act_opened' | 'story_card_click' | 'audio_play';

export function captureGoal(event: GoalEvent, properties?: Record<string, string | number | boolean>): void {
  const client = getPostHogClient();
  if (!client) {
    return;
  }
  client.capture(event, properties);
}
